import { Metamodel } from '../base/model';
import { AdapterRegistry } from './adapter-registry';
import { NamedAdapter } from './named.adapter';
import { BigDataPool, NodeSizeFamily, SparkLibraryInfo } from '../bigdatapool/bigdatapool';
import { A365BackendModel as Model } from '../a365.backendmodel';
import { Tag } from '../tags/tags';
import { annotationsToTags, tagsToAnnotations } from '../../shared/utils/tags.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(BigDataPool),
    adapts: {
        name: '*',
        location: '*',
        properties: {
            description: '*',
            autoPause: '*',
            autoScale: '*',
            nodeCount: '*',
            nodeSize: '*',
            nodeSizeFamily: '*',
            sparkVersion: '*',
            sparkEventsFolder: '*',
            libraryRequirements: '*',
            defaultSparkLogFolder: '*',
            isComputeIsolationEnabled: '*',
            sparkConfigProperties: '*',
            sessionLevelPackagesEnabled: '*',
            customLibraries: '*'
        }
    }
})
export class BigDataPoolAdapter extends NamedAdapter<BigDataPool, Model.SparkCompute> {
    protected _toResource(bigDataPool: BigDataPool): Model.SparkCompute {
        const bigDataPoolResource = super._toResource(bigDataPool) as Model.SparkCompute;
        bigDataPoolResource.location = bigDataPool.location;
        bigDataPoolResource.tags = bigDataPool.rawTags;
        bigDataPoolResource.properties = {
            autoPause: this._getAutoPause(bigDataPool),
            autoScale: this._getAutoScale(bigDataPool),
            nodeCount: bigDataPool.nodeCount,
            nodeSize: bigDataPool.nodeSize,
            nodeSizeFamily: bigDataPool.nodeSizeFamily,
            sparkVersion: bigDataPool.sparkVersion,
            sparkEventsFolder: bigDataPool.sparkEventsFolder,
            libraryRequirements: bigDataPool.libraryRequirements,
            defaultSparkLogFolder: bigDataPool.defaultSparkLogFolder,
            isComputeIsolationEnabled: bigDataPool.isComputeIsolationEnabled,
            sparkConfigProperties: bigDataPool.sparkConfigProperties,
            sessionLevelPackagesEnabled: bigDataPool.sessionLevelPackagesEnabled,
            customLibraries: bigDataPool.customLibraries && bigDataPool.customLibraries.map(this._getLibraryInfo)
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const annotations: any[] = [];
        const tagAdapter = this.context.adapterFor(Tag.MODEL);
        tagsToAnnotations(annotations, bigDataPool.tags, tagAdapter);
        tagsToAnnotations(annotations, bigDataPool.malformedTags, tagAdapter);
        this.properties(bigDataPoolResource).annotations = annotations;

        return bigDataPoolResource;
    }

    protected _toModel(bigDataPoolResource: Model.SparkCompute, bigDataPool?: BigDataPool): BigDataPool {
        if (!bigDataPool) {
            bigDataPool = BigDataPool.MODEL.create<BigDataPool>();
        }
        super._toModel(bigDataPoolResource, bigDataPool);

        const tagAdapter = this.context.adapterFor(Tag.MODEL);
        annotationsToTags(bigDataPool.tags, bigDataPool.malformedTags, bigDataPoolResource.properties?.annotations, tagAdapter);

        bigDataPool.rawTags = bigDataPoolResource.tags;
        bigDataPool.location = bigDataPoolResource.location;
        const properties = bigDataPoolResource.properties;
        if (properties) {
            const autoPause = properties.autoPause;
            if (autoPause && ((autoPause.enabled != null) || autoPause.delayInMinutes != null)) {
                bigDataPool.autoPause = {
                    enabled: autoPause.enabled,
                    delayInMinutes: autoPause.delayInMinutes
                };
            } else {
                bigDataPool.autoPause = undefined;
            }
            const autoScale = properties.autoScale;
            if (autoScale && ((autoScale.enabled != null) || autoScale.maxNodeCount != null || autoScale.minNodeCount != null)) {
                bigDataPool.autoScale = {
                    enabled: autoScale.enabled,
                    maxNodeCount: autoScale.maxNodeCount,
                    minNodeCount: autoScale.minNodeCount
                };
            } else {
                bigDataPool.autoScale = undefined;
            }
            const libReq = properties.libraryRequirements;
            if (libReq && (libReq.content != null || libReq.filename != null || libReq.time != null)) {
                bigDataPool.libraryRequirements = {
                    content: libReq.content,
                    filename: libReq.filename,
                    time: libReq.time
                };
            } else {
                bigDataPool.libraryRequirements = undefined;
            }
            bigDataPool.creationDate = properties.creationDate;
            bigDataPool.nodeCount = properties.nodeCount;
            bigDataPool.nodeSize = properties.nodeSize;
            bigDataPool.nodeSizeFamily = properties.nodeSizeFamily as NodeSizeFamily;
            bigDataPool.provisioningState = properties.provisioningState;
            bigDataPool.sparkVersion = properties.sparkVersion;
            bigDataPool.sparkEventsFolder = properties.sparkEventsFolder;
            bigDataPool.defaultSparkLogFolder = properties.defaultSparkLogFolder;
            bigDataPool.isComputeIsolationEnabled = properties.isComputeIsolationEnabled;
            bigDataPool.sparkConfigProperties = properties.sparkConfigProperties;
            bigDataPool.sessionLevelPackagesEnabled = properties.sessionLevelPackagesEnabled;
            bigDataPool.customLibraries = properties.customLibraries && properties.customLibraries.map((libraryInfo) => ({
                name: libraryInfo.name,
                path: libraryInfo.path,
                containerName: libraryInfo.containerName,
                uploadedTimestamp: libraryInfo.uploadedTimestamp,
                fileType: libraryInfo.type
            }));
        }

        return bigDataPool;
    }

    private _getAutoPause(bigDataPool: BigDataPool): Model.SparkAutoPause {
        const autoPause = bigDataPool.autoPause;
        if (autoPause && ((autoPause.enabled != null) || autoPause.delayInMinutes != null)) {
            return {
                enabled: autoPause.enabled,
                delayInMinutes: autoPause.delayInMinutes
            };
        }
        return undefined;
    }

    private _getAutoScale(bigDataPool: BigDataPool): Model.SparkAutoScale {
        const autoScale = bigDataPool.autoScale;
        if (autoScale && ((autoScale.enabled != null) || autoScale.maxNodeCount != null || autoScale.minNodeCount != null)) {
            return {
                enabled: autoScale.enabled,
                maxNodeCount: autoScale.maxNodeCount,
                minNodeCount: autoScale.minNodeCount
            };
        }
        return undefined;
    }

    private _getLibraryInfo(libraryInfo: SparkLibraryInfo): Model.SparkLibraryInfo {
        return {
            name: libraryInfo.name,
            path: libraryInfo.path,
            containerName: libraryInfo.containerName,
            uploadedTimestamp: libraryInfo.uploadedTimestamp,
            type: libraryInfo.fileType
        };
    }
}
